<template>
  <Dialog :open="showCreateOrderModal" @update:open="(e) => $emit('update:show-create-order-modal', e)">
    <DialogContent>
      <DialogHeader class="!text-center">
        <template v-if="currentTab === 'create'">
          <DialogTitle>Create Payment Plan</DialogTitle>
          <DialogDescription>Update your payment plan details.</DialogDescription>
        </template>
        <template v-if="currentTab === 'review'">
          <DialogTitle>Review Payment Plan</DialogTitle>
          <DialogDescription>Make sure the plan is correct.</DialogDescription>
        </template>
        <template v-if="currentTab === 'send'">
          <DialogTitle>Send Link</DialogTitle>
          <DialogDescription>Select a way to share the payment plan.</DialogDescription>
        </template>
        <p v-if="errMsg !== ''" class="text-error-500">{{ errMsg }}</p>
      </DialogHeader>

      <div class="h-[1px] w-full my-4 bg-grey-light-200"></div>

      <CreateOrderTab
        v-if="currentTab === 'create'"
        :order="order"
        :orderErrors="orderErrors"
        :changeDefaultOrderExpiry="changeDefaultOrderExpiry"
        :time-values="timeValues"
        :useTravelDate="useTravelDate"
        @update:date="(e) => (endDate = e)"
        @update:travelDate="(e) => (travelDate = e)"
        @update:change-default-order-expiry="(e) => (changeDefaultOrderExpiry = e)"
        :isAdmin="isAdmin"
      />
      <ReviewOrderTab
        v-if="currentTab === 'review'"
        :order="order"
        :endDate="endDate"
        :travelDate="travelDate"
        :useTravelDate="useTravelDate"
        @update:can-create-plan="(e) => (canCreatePlan = e)"
        @update:error="(e) => (errMsg = e)"
      />
      <SendOrderLinkTab
        v-if="currentTab === 'send'"
        :completed-order="completedOrder"
        :boost-rewards="isBoostRewards"
      />

      <DialogFooter>
        <template v-if="currentTab === 'create'">
          <Button variant="outline" class="w-1/2" @click="$emit('update:show-create-order-modal', false)">
            Cancel
          </Button>
          <Button class="w-1/2" @click="validateForm">Confirm</Button>
        </template>
        <template v-else-if="currentTab === 'review'">
          <Button variant="outline" @click="currentTab = 'create'">
            <ArrowLeft class="h-4 w-4" />
          </Button>
          <Button @click="createPaymentPlan" :disabled="!canCreatePlan">Create Payment Plan</Button>
        </template>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { NetworkError } from '@/errors';
import type { OrderErrors } from '@/models/orders';
import { createOrderV2 } from '@/services/orders';
import { useMerchantStore } from '@/stores/merchants';
import { useUserStore } from '@/stores/users';
import { isSafeText } from '@/utils/form.utils';
import { toCents } from '@/utils/format.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import dayjs from 'dayjs';
import { ArrowLeft } from 'lucide-vue-next';
import { reactive, ref, watch } from 'vue';
import type { ModelOrderRequest, ModelOrderResponse } from '@layup/api-sdk';
import CreateOrderTab from './tabs/CreateOrderTab.vue';
import ReviewOrderTab from './tabs/ReviewOrderTab.vue';
import SendOrderLinkTab from './tabs/SendOrderLinkTab.vue';

const props = defineProps<{
  showCreateOrderModal: boolean;
}>();

defineEmits<{
  (e: 'update:show-create-order-modal', value: boolean): void;
}>();

const merchantStore = useMerchantStore();
const userStore = useUserStore();
const isBoostRewards = ref(false);
const currentTab = ref<'create' | 'review' | 'send'>('create');
const errMsg = ref('');
const endDate = ref('');
const travelDate = ref('');
const useTravelDate = ref(false);
const changeDefaultOrderExpiry = ref(false);
const completedOrder = ref<ModelOrderResponse>();
const timeValues = reactive<{ expiryTime: string; expiryType: 'minutes' | 'hours' | 'days' }>({
  expiryTime: '0',
  expiryType: 'minutes',
});
const canCreatePlan = ref(false);
const genericOrder: Partial<ModelOrderResponse> = {
  amountDue: 0,
  endDateMax: dayjs().format('YYYY-MM-DD'),
  endDateMin: dayjs().format('YYYY-MM-DD'),
  travelDate: dayjs().format('YYYY-MM-DD'),
  products: [],
  name: '',
  state: 'PARTIAL',
  depositPerc: 20,
  reference: '',
  depositAmount: 0,
  depositType: 'PERCENTAGE',
  percFee: undefined,
  flatFee: undefined,
  imageUrl: '',
  groupPaymentPlan: {
    enabled: false,
    cloneLimit: 0,
    expiry: 0,
  },
};

let order = reactive<Partial<ModelOrderResponse>>({ ...genericOrder });
let isAdmin = ref(userStore.user?.merchantRole === 'ADMIN');

const orderErrors = reactive<OrderErrors>({
  name: '',
  reference: '',
  amountDue: '',
  depositType: '',
  depositPerc: '',
  depositAmount: '',
  orderExpiryDuration: '',
  orderExpiryTimeUnit: '',
  endDate: '',
  travelDate: '',
});

// Sets default order values from merchant when modal is shown
watch(
  () => props.showCreateOrderModal,
  (state) => {
    if (state) {
      const merchant = merchantStore.merchant;

      if (merchant.percFee) {
        order.percFee = merchant.percFee;
      }

      if (merchant.paymentPlans?.defaultDepositType?.enabled) {
        if (merchant.paymentPlans.defaultDepositType.depositType === 'INSTALMENT') {
          order.depositType = 'INSTALMENT';
        }
        if (merchant.paymentPlans.defaultDepositType.depositType === 'PERCENTAGE') {
          order.depositType = 'PERCENTAGE';
          order.depositPerc = merchant.paymentPlans.defaultDepositType.percentage ?? 20;
        }
        if (merchant.paymentPlans.defaultDepositType.depositType === 'FLAT') {
          order.depositType = 'FLAT';
          order.depositAmount = merchant.paymentPlans.defaultDepositType.flat;
        }
      }

      if (merchant.paymentPlans?.suspendOnCancel) {
        order.suspendOnCancel = merchant.paymentPlans?.suspendOnCancel;
      }
      //Admin users can go outside the end Date Range
      if (merchant.paymentPlans?.minMonths?.enabled && merchant.paymentPlans.minMonths.value && !isAdmin.value) {
        order.endDateMin = dayjs().add(merchant.paymentPlans.minMonths.value, 'month').format('YYYY-MM-DD');
      } else {
        order.endDateMin = dayjs().add(1, 'month').format('YYYY-MM-DD');
      }

      if (merchant.paymentPlans?.maxMonths?.enabled && merchant.paymentPlans.maxMonths.value && !isAdmin.value) {
        order.endDateMax = dayjs().add(merchant.paymentPlans.maxMonths.value, 'month').format('YYYY-MM-DD');
      } else {
        order.endDateMax = undefined; //this allows user to set any end date
      }
      useTravelDate.value = merchant.useTravelDate || false;
    } else {
      resetOrderForm();
    }
  },
);

const validateName = (value: string) => {
  if (!value || value.trim() === '') {
    orderErrors.name = 'Please enter a product name';
  } else if (!isSafeText(value)) {
    orderErrors.name = 'Product name contains invalid characters';
  } else {
    orderErrors.name = '';
  }
};

const validateReference = (value: string) => {
  if (!value || value.trim() === '') {
    orderErrors.reference = 'Please enter a reference number';
  } else if (!isSafeText(value)) {
    orderErrors.reference = 'Reference number contains invalid characters';
  } else {
    orderErrors.reference = '';
  }
};

const validateAmountDue = (value: number) => {
  if (isNaN(value) || value <= 0) {
    orderErrors.amountDue = 'Please enter a total order value';
  } else {
    orderErrors.amountDue = '';
  }
};

const validateDepositPercentage = (percentage: number) => {
  if (order.depositType === 'PERCENTAGE') {
    if (percentage <= 0) {
      orderErrors.depositPerc = 'Required';
    } else {
      orderErrors.depositPerc = '';
    }
  }
};

const validateDepositAmount = (amount: number) => {
  if (order.depositType === 'PERCENTAGE') {
    if (isNaN(amount) || amount <= 0) {
      orderErrors.depositAmount = 'Amount must be more than 0';
    } else {
      orderErrors.depositAmount = '';
    }
  } else if (order.depositType === 'FLAT') {
    if (isNaN(amount) || amount <= 0) {
      orderErrors.depositAmount = 'Amount must be more than 0';
    } else if (amount > (order.amountDue || 0)) {
      orderErrors.depositAmount = 'Deposit amount cannot be greater than order amount';
    } else {
      orderErrors.depositAmount = '';
    }
  }
};

const validateEndDate = (date: string) => {
  if (date === '') {
    orderErrors.endDate = 'Please select an end date';
  } else {
    orderErrors.endDate = '';
  }
};

const validateTravelDate = (date: string) => {
  if (date === '' && useTravelDate.value) {
    orderErrors.travelDate = 'Please select a travel date';
  } else {
    orderErrors.travelDate = '';
  }
};

const validateOrderExpiry = (duration: string) => {
  if (changeDefaultOrderExpiry.value) {
    if (!duration || Number(duration) <= 0) {
      orderErrors.orderExpiryDuration = 'Duration must be more than 0';
    } else {
      orderErrors.orderExpiryDuration = '';
    }
  } else {
    orderErrors.orderExpiryDuration = '';
  }
};

// Watchers for real-time validation
watch(
  () => order.name,
  (newValue) => {
    if (newValue !== undefined) {
      validateName(newValue);
    }
  },
);

watch(
  () => order.reference,
  (newValue) => {
    if (newValue !== undefined) {
      validateReference(newValue);
    }
  },
);

watch(
  () => order.amountDue,
  (newValue) => {
    if (newValue !== undefined) {
      validateAmountDue(Number(newValue));
    }
  },
);

watch(
  () => order.depositPerc,
  (newValue) => {
    if (newValue !== undefined) {
      validateDepositPercentage(newValue);
    }
  },
);

watch(
  () => order.depositAmount,
  (newValue) => {
    if (newValue !== undefined) {
      validateDepositAmount(Number(newValue));
    }
  },
);

watch(
  () => endDate.value,
  (newValue) => {
    validateEndDate(newValue);
  },
);

watch(
  () => travelDate.value,
  (newValue) => {
    validateTravelDate(newValue);
  },
);

watch(
  () => order.expire,
  (newValue) => {
    if (newValue !== undefined) {
      validateOrderExpiry(newValue);
    }
  },
);

// Watch for changes in deposit type to reset deposit amount and percentage
watch(
  () => order.amountDue,
  (amountDue) => {
    if (order.depositType === 'PERCENTAGE') {
      if (amountDue && amountDue > 0 && order.depositPerc) {
        order.depositAmount = (amountDue * order.depositPerc) / 100;
      } else {
        order.depositAmount = 0;
      }
    }
  },
  { immediate: true },
);

watch(
  () => order.depositPerc,
  (percentage) => {
    if (order.depositType === 'PERCENTAGE') {
      if (percentage && order.amountDue && order.amountDue > 0) {
        order.depositAmount = (order.amountDue * percentage) / 100;
      }
    }
  },
  { immediate: true },
);

const createPaymentPlan = async () => {
  errMsg.value = '';
  try {
    const orderRequest: ModelOrderRequest = { ...order, expire: 0 };
    if (!canCreatePlan.value) {
      return;
    }

    // Only update endDateMax on final submit so that it doesn't mess with with calendar component max range if set in orders tab in settings
    orderRequest.endDateMax = endDate.value;
    if (useTravelDate.value) {
      orderRequest.travelDate = travelDate.value;
    } else {
      delete orderRequest.travelDate;
    }

    // Populate products array because it needs to be set to create a payment plan
    if (order.amountDue && order.name) {
      orderRequest.products = [
        {
          amount: order.amountDue,
          sku: order.name,
          name: order.name,
        },
      ];
    }

    if (changeDefaultOrderExpiry.value) {
      //if order expiry is changed the convert and set
      orderRequest.expire = Number(order.expire); //value can not be 0 when order expiry is selected
    } else {
      //keep the merchant default expiry set by backend
      delete orderRequest.expire;
    }

    completedOrder.value = await createOrderV2(orderRequest);
    currentTab.value = 'send';
  } catch (error: any) {
    if (error.response?.data) {
      const serverMessage = error.response?.data || 'Unknown error';
      errMsg.value = serverMessage;
    }

    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('create-payment-plan', {
        status: 'network_error',
        channel: 'merchant',
        method: 'createOrderV2',
      });
    } else {
      createException(error);
      addMetric('create-payment-plan', {
        status: 'error',
        channel: 'merchant',
        method: 'createOrderV2',
      });
    }
  }
};

const resetFormErrors = () => {
  Object.keys(orderErrors).forEach((key) => {
    orderErrors[key as keyof typeof orderErrors] = '';
  });
};

const resetOrderForm = () => {
  // ⚠️ Do not reassign order = { ...genericOrder } — preserve the reactive proxy to keep Vue watchers and reactivity working.
  // Use Object.assign to reset fields while maintaining the original reactive object.
  Object.assign(order, { ...genericOrder });
  changeDefaultOrderExpiry.value = false;
  resetFormErrors();
  currentTab.value = 'create';
};

const validateForm = async () => {
  validateName(order.name || '');
  validateReference(order.reference || '');
  validateAmountDue(Number(order.amountDue || 0));
  validateDepositPercentage(order.depositPerc || 0);
  validateDepositAmount(Number(order.depositAmount || 0));
  validateEndDate(endDate.value);
  validateTravelDate(travelDate.value);
  validateOrderExpiry(order.expire || '');

  const hasError = Object.values(orderErrors).some((val) => val !== '');

  if (!hasError) {
    // Convert to cents and proceed
    const depositAmount = Number(order.depositAmount);
    const amountDue = Number(order.amountDue);
    order.amountDue = toCents(amountDue);
    order.depositAmount = toCents(depositAmount);
    currentTab.value = 'review';
  }
};
</script>
